import React from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, typography, spacing, radius } from '../design-system';

const S = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
    background: colors.bgPage || '#f5f6f8',
  },
  wrap: {
    maxWidth: 860,
    margin: '0 auto',
    background: '#fff',
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderLight}`,
    padding: '48px 56px',
  },
  h1: {
    margin: '0 0 4px 0',
    fontSize: '28px',
    fontWeight: typography.fontWeights.bold,
    color: colors.textPrimary,
  },
  meta: {
    fontSize: typography.fontSizes.sm,
    color: colors.textMuted,
    marginBottom: 40,
  },
  h2: {
    fontSize: '18px',
    fontWeight: typography.fontWeights.semibold,
    color: colors.textPrimary,
    margin: '36px 0 12px 0',
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  h3: {
    fontSize: typography.fontSizes.base,
    fontWeight: typography.fontWeights.semibold,
    color: colors.textPrimary,
    margin: '20px 0 8px 0',
  },
  p: {
    margin: '0 0 12px 0',
    lineHeight: 1.7,
    color: colors.textSecondary,
    fontSize: typography.fontSizes.sm,
  },
  ul: {
    margin: '0 0 12px 0',
    paddingLeft: 24,
    lineHeight: 1.8,
    color: colors.textSecondary,
    fontSize: typography.fontSizes.sm,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: typography.fontSizes.sm,
    marginBottom: 16,
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    background: colors.bgSubtle || '#f8f9fa',
    fontWeight: typography.fontWeights.semibold,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    fontSize: typography.fontSizes.xs,
  },
  td: {
    padding: '8px 12px',
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.borderLight}`,
    verticalAlign: 'top',
    lineHeight: 1.5,
  },
  infoBox: {
    background: colors.bgSubtle || '#f8f9fa',
    border: `1px solid ${colors.borderLight}`,
    borderRadius: radius.sm,
    padding: '16px 20px',
    margin: '12px 0',
    fontSize: typography.fontSizes.sm,
    color: colors.textSecondary,
    lineHeight: 1.7,
  },
};

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        <h1 style={S.h1}>Privacy Notice</h1>
        <p style={S.meta}>
          ICSC BOSS Platform &nbsp;·&nbsp; Last updated: 16 April 2026 &nbsp;·&nbsp; Version 1.0
        </p>

        {/* 1 */}
        <h2 style={S.h2}>1. Who We Are</h2>
        <p style={S.p}>
          ICSC (International Standards Certification Council) is the data controller for personal data processed through the ICSC BOSS platform. ICSC BOSS is used to manage accreditation applications, quality assurance processes, trainer and assessor records, and governance activities.
        </p>
        <div style={S.infoBox}>
          <strong>Data Controller:</strong> ICSC — International Standards Certification Council<br />
          <strong>Data Protection Officer:</strong> dpo@icsc.org.uk<br />
          <strong>General enquiries:</strong> privacy@icsc.org.uk<br />
          <strong>ICO registration:</strong> [Registration number — confirm with DPO]
        </div>

        {/* 2 */}
        <h2 style={S.h2}>2. What Data We Collect and Why</h2>
        <p style={S.p}>We collect and process personal data across the following areas of the platform:</p>

        <h3 style={S.h3}>2.1 Platform Users (all roles)</h3>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Data</th>
              <th style={S.th}>Purpose</th>
              <th style={S.th}>Lawful Basis</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Name, email address, job title", "Account creation, authentication, and communication", "Contract"],
              ["Role and access permissions", "Role-based access control", "Contract"],
              ["Login timestamps, IP address", "Security monitoring and audit trail", "Legitimate interests"],
              ["Activity log (actions taken in system)", "Governance audit trail; security", "Legitimate interests / Legal obligation"],
            ].map(([d, p, b], i) => (
              <tr key={i}>
                <td style={S.td}>{d}</td>
                <td style={S.td}>{p}</td>
                <td style={S.td}>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={S.h3}>2.2 Providers</h3>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Data</th>
              <th style={S.th}>Purpose</th>
              <th style={S.th}>Lawful Basis</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Organisation name, address, contact details", "Accreditation application and administration", "Legitimate interests"],
              ["Accreditation application and decision history", "Accreditation lifecycle management", "Legitimate interests"],
              ["Uploaded evidence documents", "Accreditation review", "Legitimate interests"],
              ["Correspondence and decision letters", "Accreditation record", "Legitimate interests / Legal obligation"],
            ].map(([d, p, b], i) => (
              <tr key={i}>
                <td style={S.td}>{d}</td>
                <td style={S.td}>{p}</td>
                <td style={S.td}>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={S.h3}>2.3 Trainers and Assessors</h3>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Data</th>
              <th style={S.th}>Purpose</th>
              <th style={S.th}>Lawful Basis</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Name, contact details, qualifications", "Approval and monitoring of trainers/assessors", "Legitimate interests"],
              ["CV and qualification certificates", "Competence verification", "Legitimate interests"],
              ["CPD records", "Annual CPD compliance monitoring", "Legitimate interests"],
              ["IQA observation records", "Quality assurance", "Legitimate interests"],
              ["Conflict of interest declarations", "Impartiality management", "Legal obligation / Legitimate interests"],
              ["DBS check outcome (outcome only, not certificate)", "Safeguarding compliance", "Legal obligation"],
            ].map(([d, p, b], i) => (
              <tr key={i}>
                <td style={S.td}>{d}</td>
                <td style={S.td}>{p}</td>
                <td style={S.td}>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={S.h3}>2.4 Learners</h3>
        <p style={S.p}>
          ICSC does not collect learner names directly. Learner data within the system is pseudonymised — learners are identified by a reference number assigned by the provider. Where a reasonable adjustment request is submitted on behalf of a learner, health or disability information may be processed. This is done on the basis of explicit consent provided via the provider, and access is restricted to authorised ICSC staff only.
        </p>

        <h3 style={S.h3}>2.5 ICSC Staff and Committee Members</h3>
        <p style={S.p}>
          Name, contact details, role, and conflict of interest declarations are held for governance and operational purposes. This processing is necessary for the performance of employment and committee obligations.
        </p>

        {/* 3 */}
        <h2 style={S.h2}>3. Special Category Data</h2>
        <p style={S.p}>
          Special category data (health, disability, criminal convictions) is processed only where specifically required:
        </p>
        <ul style={S.ul}>
          <li><strong>Health and disability data</strong> — processed only where included in a reasonable adjustment request, on the basis of explicit consent. Access is restricted in ICSC BOSS.</li>
          <li><strong>Criminal convictions data</strong> — DBS check outcome records are held for staff and assessors in roles involving contact with learners. Only the outcome is retained; the certificate is returned to the individual.</li>
          <li><strong>Safeguarding records</strong> — held in a restricted-access safeguarding log, separate from general accreditation records.</li>
        </ul>

        {/* 4 */}
        <h2 style={S.h2}>4. How Long We Keep Your Data</h2>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Data Category</th>
              <th style={S.th}>Retention Period</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Accreditation records (active)", "Duration of accreditation + 6 years"],
              ["Accreditation records (rejected / withdrawn)", "6 years from decision date"],
              ["Evidence files", "Duration of accreditation + 6 years"],
              ["Trainer / assessor approval records", "Duration of approval + 6 years"],
              ["IQA and observation records", "6 years"],
              ["CPD logs", "6 years"],
              ["Committee meeting minutes and governance records", "10 years"],
              ["Conflict of interest declarations", "6 years"],
              ["Complaints and appeals records", "6 years from closure"],
              ["Safeguarding records", "10 years or until subject age 25, whichever is later"],
              ["Platform audit log", "10 years"],
              ["Application / security logs", "90 days"],
              ["Financial records", "7 years"],
            ].map(([cat, ret], i) => (
              <tr key={i}>
                <td style={S.td}>{cat}</td>
                <td style={S.td}>{ret}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={S.p}>
          On expiry of its retention period, personal data is securely deleted or anonymised. Full details are in the ICSC Document Retention Policy (POL-008), available from the Document Library.
        </p>

        {/* 5 */}
        <h2 style={S.h2}>5. Who We Share Your Data With</h2>
        <p style={S.p}>We do not sell personal data. Data is shared only in the following circumstances:</p>
        <ul style={S.ul}>
          <li><strong>Within ICSC:</strong> Staff and committee members access data only on a need-to-know basis, controlled by role-based permissions in ICSC BOSS.</li>
          <li><strong>Contracted processors:</strong> Hosting, email delivery, and backup services operate under Data Processing Agreements and may not process data for their own purposes.</li>
          <li><strong>Regulatory and statutory authorities:</strong> Where required by law (e.g., ICO, law enforcement, safeguarding referrals to LADO or adult social care).</li>
          <li><strong>Appeal and complaint panels:</strong> Relevant records are shared with independent panel members under strict confidentiality obligations.</li>
        </ul>
        <p style={S.p}>Data is not transferred outside the UK or EEA without appropriate safeguards (Standard Contractual Clauses or UK adequacy decision).</p>

        {/* 6 */}
        <h2 style={S.h2}>6. Your Rights</h2>
        <p style={S.p}>Under UK GDPR you have the following rights. To exercise any of them, contact the DPO at <strong>dpo@icsc.org.uk</strong>. We will respond within 30 calendar days.</p>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Right</th>
              <th style={S.th}>What it means</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Access", "Receive a copy of the personal data we hold about you"],
              ["Rectification", "Have inaccurate data corrected"],
              ["Erasure", "Have data deleted where there is no overriding legal obligation to retain it"],
              ["Restriction", "Limit how we process your data while a dispute is resolved"],
              ["Portability", "Receive your data in a structured, machine-readable format (where processing is automated and based on contract or consent)"],
              ["Object", "Object to processing based on legitimate interests"],
              ["Withdraw consent", "Withdraw consent at any time where processing relies on consent"],
            ].map(([r, m], i) => (
              <tr key={i}>
                <td style={{ ...S.td, fontWeight: typography.fontWeights.semibold, width: 160 }}>{r}</td>
                <td style={S.td}>{m}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={S.p}>
          If you are not satisfied with our response you have the right to lodge a complaint with the <strong>Information Commissioner's Office (ICO)</strong> at ico.org.uk or by calling 0303 123 1113.
        </p>

        {/* 7 */}
        <h2 style={S.h2}>7. Security</h2>
        <p style={S.p}>ICSC BOSS implements the following security measures to protect your personal data:</p>
        <ul style={S.ul}>
          <li>All data in transit is protected by TLS 1.2 or higher</li>
          <li>Data at rest is encrypted using AES-256</li>
          <li>Passwords are hashed using bcrypt (minimum cost factor 12); plain-text passwords are never stored</li>
          <li>Access is controlled by role-based permissions; users can only access data relevant to their role</li>
          <li>All data-modifying actions are recorded in an immutable audit log</li>
          <li>Accounts are locked after five consecutive failed login attempts</li>
          <li>Regular backups with tested recovery procedures</li>
        </ul>

        {/* 8 */}
        <h2 style={S.h2}>8. Cookies</h2>
        <p style={S.p}>
          ICSC BOSS uses authentication tokens (JWT) stored in browser localStorage for session management. These are essential for the platform to function and cannot be disabled. No advertising or third-party tracking cookies are used.
        </p>

        {/* 9 */}
        <h2 style={S.h2}>9. Changes to This Notice</h2>
        <p style={S.p}>
          This Privacy Notice is reviewed annually and updated when processing activities change materially. The version number and last-updated date at the top of this page indicate the current version. Material changes are communicated to users via the platform.
        </p>

        {/* 10 */}
        <h2 style={S.h2}>10. Contact</h2>
        <div style={S.infoBox}>
          <strong>Data Protection Officer</strong><br />
          ICSC — International Standards Certification Council<br />
          Email: dpo@icsc.org.uk<br />
          General privacy enquiries: privacy@icsc.org.uk<br /><br />
          <strong>ICO (supervisory authority):</strong><br />
          Information Commissioner's Office &nbsp;·&nbsp; ico.org.uk &nbsp;·&nbsp; 0303 123 1113
        </div>

        <div style={{ marginTop: 40, paddingTop: 24, borderTop: `1px solid ${colors.borderLight}` }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '10px 24px',
              border: `1px solid ${colors.borderLight}`,
              borderRadius: radius.sm,
              background: 'transparent',
              color: colors.textPrimary,
              cursor: 'pointer',
              fontSize: typography.fontSizes.sm,
              fontWeight: typography.fontWeights.semibold,
            }}
          >
            Back
          </button>
        </div>

      </div>
    </div>
  );
}
